import { Reference } from '@approvalmax/types';
import { DateTimeEditor, DropdownEditor, Field, TextEditor } from '@approvalmax/ui';
import { Box, Flex, Grid, Label, Text } from '@approvalmax/ui/src/components';
import { constants, statics } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { getIsExchangeRateInputAvailable } from 'pages/requestForm/selectors/requestSelectors';
import { memo, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import {
    changeExchangeRateParams,
    changeQbooksRequestCurrency,
    ExchangeRateParams,
    updateQBooksLineItemsWithCurrencyExchangeRate,
} from '../../../actions';
import { CurrencyDisclaimerLegacy } from '../../../components/(legacy)/CurrencyDisclaimerLegacy/CurrencyDisclaimerLegacy';
import CurrencyExchangeRateInput from '../../../components/CurrencyExchangeRateInput/CurrencyExchangeRateInput';
import { ExchangeRateWithUpdate } from '../../../components/ExchangeRateWithUpdate/ExchangeRateWithUpdate';
import { SectionContainer } from '../../../components/SectionContainer';
import {
    useCoreData,
    useDepartment,
    usePayee,
    usePayeeItems,
    usePayeeType,
    usePaymentAccount,
    usePaymentAccountItems,
    usePaymentDate,
    usePaymentMethod,
    usePaymentType,
    useRefNo,
} from './QBooksExpenseFieldsSection.hooks';
import { messages } from './QBooksExpenseFieldsSection.messages';

export const QBooksExpenseFieldsSection = memo(() => {
    const dispatch = useDispatch();

    const { request } = useCoreData();

    const {
        paymentAccount,
        paymentAccountPlaceholder,
        isInvalidPaymentAccount,
        onPaymentAccountChange,
        paymentAccountCurrency,
        paymentAccountType,
    } = usePaymentAccount();

    const {
        payeeType,
        onChangePayeeType,
        payeeTypePlaceholder,
        isDisabledPayeeType,
        isRequiredPayeeType,
        isInvalidPayeeType,
        payeeTypeItems,
    } = usePayeeType(paymentAccountCurrency);

    const {
        payee,
        payeePlaceholder,
        isDisabledPayee,
        isRequiredPayee,
        isInvalidPayee,
        canCreateNewVendorFn,
        createNewVendorTextFn,
        onCreateVendor,
        onChangePayee,
        payeeCurrency,
    } = usePayee(onPaymentAccountChange, paymentAccountCurrency);

    const { payees, loadingPayees } = usePayeeItems(paymentAccountCurrency?.id);
    const { paymentAccounts, loadingPaymentAccounts } = usePaymentAccountItems(payeeCurrency?.id);

    const {
        paymentMethod,
        paymentMethodItems,
        paymentMethodPlaceholder,
        onChangePaymentMethod,
        isRequiredPaymentMethod,
        isInvalidPaymentMethod,
    } = usePaymentMethod();

    const { paymentType, paymentTypeItems, paymentTypePlaceholder, isInvalidPaymentType, onChangePaymentType } =
        usePaymentType(paymentAccountType);

    const { paymentDate, paymentDatePlaceholder, isInvalidPaymentDate, onChangePaymentDate } = usePaymentDate();

    const { refNo, refNoPlaceholder, onChangeRefNo } = useRefNo();
    const isExchangeRateInputAvailable = getIsExchangeRateInputAvailable(request);

    const {
        department,
        departmentItems,
        isShowDepartment,
        departmentLabel,
        departmentPlaceholder,
        onChangeDepartment,
        isRequiredDepartment,
        isInvalidDepartment,
    } = useDepartment();

    const changeRequestCurrency = useCallback(
        (currency: Reference) => {
            dispatch(changeQbooksRequestCurrency(currency));
        },
        [dispatch]
    );

    const onExchangeRateChange = useCallback(
        (params: ExchangeRateParams) => {
            dispatch(changeExchangeRateParams(params));
            dispatch(updateQBooksLineItemsWithCurrencyExchangeRate(params.exchangeRate ?? null));
        },
        [dispatch]
    );

    const handleManualChangeExchangeRate = useCallback(
        (value?: number) => {
            if (value) {
                onExchangeRateChange({
                    exchangeRate: value,
                    exchangeRateSource: domain.ExchangeRateSource.Manual,
                    exchangeRateDate: null,
                });
            } else {
                onExchangeRateChange({
                    exchangeRate: null,
                    exchangeRateSource: domain.ExchangeRateSource.None,
                    exchangeRateDate: null,
                });
            }
        },
        [onExchangeRateChange]
    );

    useEffect(() => {
        if (request.currency === request.companyCurrency && request.exchangeRate) {
            onExchangeRateChange({
                exchangeRate: null,
                exchangeRateSource: domain.ExchangeRateSource.None,
                exchangeRateDate: null,
            });
        }
    }, [payeeCurrency, request, onExchangeRateChange]);

    useEffect(() => {
        const companyCurrency = request.companyCurrency;
        const currentRequestCurrency = request.currency;

        let newCurrency: Reference = {
            id: companyCurrency,
            text: companyCurrency,
        };

        if (payeeCurrency) {
            newCurrency = payeeCurrency;
        } else if (paymentAccountCurrency) {
            newCurrency = paymentAccountCurrency;
        }

        if (currentRequestCurrency !== newCurrency.id) {
            changeRequestCurrency(newCurrency);
        }
    }, [payeeCurrency, paymentAccountCurrency, request.companyCurrency, request.currency, changeRequestCurrency]);

    return (
        <SectionContainer title={messages.sectionName}>
            <Flex direction='column' spacing='8 0'>
                <Flex container spacing='20'>
                    <Flex size={6} block>
                        <Field title={messages.payeeTypeFieldTitle} required={isRequiredPayeeType}>
                            <PlainDataProvider items={payeeTypeItems}>
                                <DropdownEditor
                                    value={payeeType}
                                    disabled={isDisabledPayeeType}
                                    invalid={isInvalidPayeeType}
                                    onChange={onChangePayeeType}
                                    placeholder={payeeTypePlaceholder}
                                />
                            </PlainDataProvider>

                            {payee?.address && payee.address.length > 0 && (
                                <Box spacing='8 0 0'>
                                    <Flex spacing='0 4' wrap={false}>
                                        <Label>{messages.addressLabel}</Label>

                                        <Flex spacing='4 0' direction='column'>
                                            {payee.address.map((address, index) => (
                                                <Text key={index} font='label' fontSize='small'>
                                                    {address}
                                                </Text>
                                            ))}
                                        </Flex>
                                    </Flex>
                                </Box>
                            )}
                        </Field>
                    </Flex>

                    <Flex size={6} block>
                        <Field title={messages.payeeFieldTitle} required={isRequiredPayee}>
                            <PlainDataProvider items={payees}>
                                <DropdownEditor
                                    value={payee}
                                    singleLinePerItem={false}
                                    onChange={onChangePayee}
                                    placeholder={payeePlaceholder}
                                    invalid={isInvalidPayee}
                                    disabled={isDisabledPayee}
                                    canCreateItemFn={canCreateNewVendorFn}
                                    createItemDisplayTextFn={createNewVendorTextFn}
                                    onCreateItem={onCreateVendor}
                                    loading={loadingPayees}
                                />
                            </PlainDataProvider>

                            {(payee?.email || payee?.taxId) && (
                                <Box spacing='8 0 0'>
                                    <Grid gridTemplateColumns='auto 1fr' columnGap={4}>
                                        {payee?.email && (
                                            <>
                                                <Label>{messages.emailLabel}</Label>

                                                <Text font='label' fontSize='small'>
                                                    {payee?.email}
                                                </Text>
                                            </>
                                        )}

                                        {payee?.taxId && (
                                            <>
                                                <Label>{messages.taxIdLabel}</Label>

                                                <Text font='label' fontSize='small'>
                                                    {payee?.taxId}
                                                </Text>
                                            </>
                                        )}
                                    </Grid>
                                </Box>
                            )}
                        </Field>
                    </Flex>

                    <Flex size={12} block>
                        <Field title={<span>&nbsp;</span>}>
                            {isExchangeRateInputAvailable && (
                                <CurrencyExchangeRateInput
                                    defaultCurrency={
                                        statics.currency.findCurrencyByCode(request.companyCurrency) || {
                                            id: request.companyCurrency,
                                            text: request.companyCurrency,
                                        }
                                    }
                                    selectedCurrency={
                                        statics.currency.findCurrencyByCode(request.currency) || {
                                            id: request.currency,
                                            text: request.currency,
                                        }
                                    }
                                    exchangeRateValue={
                                        request.exchangeRateSource === domain.ExchangeRateSource.Manual
                                            ? request.exchangeRate
                                            : null
                                    }
                                    precision={7}
                                    onExchangeRateChange={handleManualChangeExchangeRate}
                                />
                            )}

                            <Box spacing='4 0 0'>
                                {request.exchangeRateSource === domain.ExchangeRateSource.Manual ? (
                                    <CurrencyDisclaimerLegacy />
                                ) : (
                                    <ExchangeRateWithUpdate
                                        onChange={onExchangeRateChange}
                                        requestId={request.id}
                                        companyId={request.companyId}
                                        date={request.details.date}
                                        currency={request.currency}
                                        companyCurrency={request.companyCurrency}
                                    />
                                )}
                            </Box>
                        </Field>
                    </Flex>
                </Flex>

                <Flex container spacing='20'>
                    <Flex size={6} block>
                        <Field title={messages.paymentAccountFieldTitle} required>
                            <PlainDataProvider items={paymentAccounts}>
                                <DropdownEditor
                                    invalid={isInvalidPaymentAccount}
                                    value={paymentAccount}
                                    singleLinePerItem={false}
                                    onChange={onPaymentAccountChange}
                                    placeholder={paymentAccountPlaceholder}
                                    displaySubAttribute='normalizedType'
                                    loading={loadingPaymentAccounts}
                                />
                            </PlainDataProvider>
                        </Field>
                    </Flex>

                    <Flex size={6} block>
                        <Field title={messages.paymentTypeField} required>
                            <PlainDataProvider items={paymentTypeItems}>
                                <DropdownEditor
                                    invalid={isInvalidPaymentType}
                                    value={paymentType}
                                    onChange={onChangePaymentType}
                                    placeholder={paymentTypePlaceholder}
                                />
                            </PlainDataProvider>
                        </Field>
                    </Flex>

                    <Flex size={6} block>
                        <Field title={messages.paymentMethodField} required={isRequiredPaymentMethod}>
                            <PlainDataProvider items={paymentMethodItems}>
                                <DropdownEditor
                                    value={paymentMethod}
                                    singleLinePerItem={false}
                                    onChange={onChangePaymentMethod}
                                    placeholder={paymentMethodPlaceholder}
                                    invalid={isInvalidPaymentMethod}
                                />
                            </PlainDataProvider>
                        </Field>
                    </Flex>

                    <Flex size={6} block>
                        <Field title={messages.paymentDateField} required>
                            <DateTimeEditor
                                invalid={isInvalidPaymentDate}
                                value={paymentDate}
                                onChange={onChangePaymentDate}
                                placeholder={paymentDatePlaceholder}
                            />
                        </Field>
                    </Flex>
                </Flex>

                <Flex container spacing='20'>
                    <Flex size={6} block>
                        <Field title={messages.refNoField}>
                            <TextEditor
                                value={refNo}
                                onChange={onChangeRefNo}
                                maxLength={constants.qBooksConstants.REFERENCE_MAX_LENGTH}
                                placeholder={refNoPlaceholder}
                                changeOnBlur
                            />
                        </Field>
                    </Flex>

                    {isShowDepartment && (
                        <Flex size={6} block>
                            <Field title={departmentLabel} required={isRequiredDepartment}>
                                <PlainDataProvider items={departmentItems}>
                                    <DropdownEditor
                                        value={department}
                                        onChange={onChangeDepartment}
                                        placeholder={departmentPlaceholder}
                                        invalid={isInvalidDepartment}
                                    />
                                </PlainDataProvider>
                            </Field>
                        </Flex>
                    )}
                </Flex>
            </Flex>
        </SectionContainer>
    );
});

QBooksExpenseFieldsSection.displayName = 'QBooksExpenseFieldsSection';
